import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { RiShoppingBag3Line, RiCheckboxCircleLine, RiCloseLine, RiHistoryLine } from 'react-icons/ri';
import productService, { Product, ProductForm } from '../../services/productService';
import codeGeneratorService from '../../services/codeGeneratorService';
import { useUnsavedChanges } from '../../hooks/useUnsavedChanges';
import { usePermission } from '../../hooks/usePermission';
import { queryKeys } from '../../queryClient';
import { PRODUCTS_PATH, productDetailUrl } from '../../utils/productLinks';
import { parseRouteId } from '../../utils/routeId';
import { showAlert } from '../../utils/alert';
import { mapFieldErrors, focusFirstInvalidField } from '../../utils/mapFieldErrors';
import ConfirmModal from '../../components/ui/ConfirmModal';
import ProductFormFields from '../../components/product/ProductFormFields';
import {
  emptyProductForm,
  productFromApi,
  productToForm,
  validateProductForm,
  productIfMatch,
  PRODUCT_FIELD_LABELS,
} from '../../components/product/productForm';
import {
  DetailPageLayout,
  DetailSection,
  RecordMetaCard,
  EntityActivityTimeline,
  useDetailNavigation,
} from '../../components/detail';

const LIST_PATH = PRODUCTS_PATH;

/**
 * หน้าเพิ่ม/แก้ไขสินค้า — `/master-data/products/new` · `/master-data/products/:id/edit`
 * (แทนโมดัลเพิ่ม/แก้ไขใน ProductPage · มติ PO 28 ต.ค. 2026 · หน้าตาแบบ MaintenanceFormPage)
 *
 * ตรรกะบันทึกยกมาจาก handleSaveProduct ของโมดัลเดิมครบ: กันกดซ้ำ (ref) · If-Match (409 → โหลดค่าล่าสุดลงฟอร์มใหม่) ·
 * 404 (ถูกลบไปแล้ว) → กลับหน้ารายการ · 422 field_errors → ใต้ช่อง · ชื่อซ้ำ → ใต้ช่องชื่อ ·
 * รหัสซ้ำ → ออกรหัสใหม่ เตือนใต้ช่อง รอผู้ใช้กดบันทึกอีกครั้ง · ที่เหลือ → alert ข้อความจริงจาก backend
 */
const ProductFormPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const isEdit = id !== undefined;
  const productId = isEdit ? parseRouteId(id) : null;
  const { hasPermission } = usePermission();
  const queryClient = useQueryClient();
  const { backTo, goTo } = useDetailNavigation(LIST_PATH);

  const [formData, setFormData] = useState<ProductForm>(() => emptyProductForm());
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [codeError, setCodeError] = useState<string>('');
  /** ค่าตั้งต้นของฟอร์ม (JSON) — เทียบเพื่อรู้ว่าผู้ใช้แก้แล้วหรือยัง · null = ยังโหลดไม่เสร็จ */
  const [initialSnapshot, setInitialSnapshot] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);

  const productQuery = useQuery({
    queryKey: [...queryKeys.products.all, 'detail', productId ?? 0],
    queryFn: async () => productFromApi(await productService.getProduct(productId!)),
    enabled: isEdit && productId !== null,
    // หน้าแก้ไขต้องได้ token If-Match ล่าสุดเสมอ — ไม่ใช้ cache เก่าจากหน้าดู
    staleTime: 0,
    refetchOnWindowFocus: false,
  });
  const product: Product | undefined = productQuery.data;

  // ตั้งค่าฟอร์มครั้งเดียวต่อสินค้า (refetch ภายหลังไม่ทับสิ่งที่ผู้ใช้กำลังพิมพ์)
  const initializedFor = useRef<number | 'new' | null>(null);
  useEffect(() => {
    if (isEdit) {
      if (!product || initializedFor.current === product.id) return;
      initializedFor.current = product.id;
      const f = productToForm(product);
      setFormData(f);
      setInitialSnapshot(JSON.stringify(f));
      return;
    }
    if (initializedFor.current === 'new') return;
    initializedFor.current = 'new';
    // ใช้ preview รหัสถัดไปแทนการสร้างรหัสจริง (รหัสจริงออกตอนบันทึก)
    codeGeneratorService
      .getNextCodePreview('product', true)
      .then((r) => r.next_code)
      .catch((error) => {
        console.error('Error getting next code preview:', error);
        return ''; // fallback to empty
      })
      .then((code) => {
        const f = emptyProductForm(code);
        setFormData(f);
        setInitialSnapshot(JSON.stringify(f));
      });
  }, [isEdit, product]);

  const isDirty = !saved && initialSnapshot !== null && JSON.stringify(formData) !== initialSnapshot;
  const { showModal, onConfirmLeave, onCancelLeave } = useUnsavedChanges(isDirty);

  const cancelTo = isEdit && productId ? productDetailUrl(productId) : backTo;
  const isSubmittingRef = useRef(false); // กันกดบันทึกซ้ำ same-tick

  const handleSave = async () => {
    if (isSubmittingRef.current) return;

    const errors = validateProductForm(formData);
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      setTimeout(() => {
        const el = document.querySelector(`[name="${Object.keys(errors)[0]}"]`) as HTMLInputElement | null;
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          el.focus();
        }
      }, 50);
      return;
    }
    setFormErrors({});

    isSubmittingRef.current = true;
    setSaving(true);
    try {
      let savedProduct: Product;
      if (!isEdit) {
        savedProduct = await productService.createProduct(formData);
        // KD3 S2121 (BUG-S2121-002): แสดงเสมอ
        showAlert('สำเร็จ', 'สร้างสินค้าสำเร็จ', 'success');
      } else {
        if (!product) return;
        // P-S468-1: แนบ updated_at ที่โหลดมา → server ตรวจ 409 กันแก้ทับกันเงียบ
        // payload = เฉพาะช่องที่อยู่บนฟอร์ม (productService.updateProduct แปลงเป็น snake_case)
        savedProduct = await productService.updateProduct(product.id, formData, productIfMatch(product));
        showAlert('สำเร็จ', 'แก้ไขสินค้าสำเร็จ', 'success');
      }
      setCodeError('');
      setSaved(true); // ปลดตัวกันออกจากหน้า ก่อนเปลี่ยนหน้า
      queryClient.invalidateQueries({ queryKey: queryKeys.products.all });
      const savedId = savedProduct?.id ?? product?.id;
      if (savedId && hasPermission('products.view')) goTo(productDetailUrl(savedId), { replace: true });
      else goTo(backTo, { replace: true });
    } catch (error: any) {
      console.error('Save error:', error);

      // P-S601-1: 409 stale-edit — มีคนแก้สินค้านี้ไปก่อน · โหลดค่าล่าสุดลงฟอร์มใหม่ (ได้ token ใหม่ด้วย)
      // ห้ามคงค่าที่พิมพ์ไว้แล้วให้กดซ้ำด้วย token ใหม่ = เขียนทับงานของอีกคนโดยไม่เห็นว่าเขาแก้อะไร
      if (error.response?.status === 409) {
        showAlert('ข้อมูลถูกแก้ไขโดยผู้ใช้อื่น', 'ระบบโหลดข้อมูลล่าสุดลงฟอร์มให้แล้ว กรุณาตรวจสอบแล้วแก้ไขใหม่อีกครั้ง', 'error');
        initializedFor.current = null;
        productQuery.refetch();
        return;
      }

      // KD3 S2111 (BUG-S2111-003): แก้สินค้าที่ถูกลบไปแล้ว (อีกแท็บ/อีกคน) → 404 · ไม่ปล่อยฟอร์มค้างให้กดซ้ำวน
      // กลับหน้ารายการ (โหลดใหม่ แถวผีหาย)
      if (error.response?.status === 404 && isEdit) {
        showAlert('ไม่พบข้อมูล', 'ไม่พบสินค้านี้ อาจถูกลบไปแล้ว', 'error');
        setSaved(true);
        queryClient.invalidateQueries({ queryKey: queryKeys.products.all });
        goTo(LIST_PATH, { replace: true });
        return;
      }

      // BUG-S1162-001 PROPAGATION (S1165): server 422 field_errors → inline (WCAG 3.3.1)
      const fieldErrMap = mapFieldErrors(error);
      if (fieldErrMap) {
        setFormErrors((prev) => ({ ...prev, ...fieldErrMap }));
        focusFirstInvalidField(fieldErrMap);
        return;
      }

      const errorMessage: string =
        error.response?.data?.error?.user_message ||
        error.response?.data?.error?.message ||
        (typeof error.response?.data?.detail === 'string' ? error.response.data.detail : '') ||
        '';

      if (errorMessage.includes('ชื่อ') && errorMessage.includes('มีอยู่ในระบบแล้ว')) {
        // OBS-S1109-001 FIX (S1113): name dup → inline field error
        setFormErrors((prev) => ({ ...prev, name: errorMessage }));
        const nameEl = document.getElementsByName('name')[0] as HTMLInputElement | undefined;
        if (nameEl) nameEl.focus();
      } else if (!isEdit && errorMessage.includes('รหัส') && errorMessage.includes('มีอยู่ในระบบแล้ว')) {
        // รหัสซ้ำ (BUG-S2102-001) → สร้างรหัสใหม่ เตือนใต้ช่อง รอผู้ใช้กดบันทึกอีกครั้ง (ไม่บันทึกอัตโนมัติ)
        const oldCode = formData.code;
        try {
          const newCode = await codeGeneratorService.generateSingleCode('product');
          setFormData((prev) => ({ ...prev, code: newCode }));
          setCodeError(`รหัส ${oldCode} ไม่ว่างเนื่องจากมีการใช้งานแล้ว กรุณาบันทึกใหม่`);
        } catch (genError) {
          console.error('Error generating new code:', genError);
          setCodeError('ไม่สามารถสร้างรหัสใหม่ได้ กรุณาลองใหม่');
        }
      } else {
        // ข้อความจริงจาก backend ถ้ามี (user bug Tirawat 2026-08-26)
        showAlert('เกิดข้อผิดพลาด', errorMessage || 'ไม่สามารถบันทึกข้อมูลได้', 'error');
      }
    } finally {
      isSubmittingRef.current = false;
      setSaving(false);
    }
  };

  const title = isEdit ? 'แก้ไขข้อมูลสินค้า' : 'เพิ่มสินค้าใหม่';
  const breadcrumbs = useMemo(() => [
    { label: 'สินค้า', path: backTo },
    ...(isEdit && product ? [{ label: product.name, path: productDetailUrl(product.id) }] : []),
    { label: isEdit ? 'แก้ไข' : 'เพิ่มใหม่' },
  ], [backTo, isEdit, product]);

  const notFound = isEdit && productId === null ? { response: { status: 404 } } : null;

  return (
    <DetailPageLayout
      breadcrumbs={breadcrumbs}
      icon={<RiShoppingBag3Line />}
      title={title}
      recordId={isEdit ? product?.code || productId : undefined}
      subtitle={isEdit ? product?.name : 'กรอกข้อมูลสินค้า (รหัสสร้างอัตโนมัติ)'}
      backTo={cancelTo}
      loading={(isEdit && productQuery.isLoading) || (initialSnapshot === null && !notFound && !productQuery.isError)}
      error={notFound || productQuery.error}
      notFoundMessage="ไม่พบสินค้าที่ระบุ (อาจถูกลบไปแล้ว)"
      onRetry={() => productQuery.refetch()}
      overlays={
        <ConfirmModal
          isOpen={showModal}
          onClose={onCancelLeave}
          onConfirm={onConfirmLeave}
          pushHistoryOnOpen={false}
          title="ข้อมูลยังไม่ได้บันทึก"
          message="คุณมีข้อมูลที่ยังไม่ได้บันทึก หากออกจากหน้านี้ข้อมูลที่กรอกจะหายไป ต้องการออกจากหน้านี้หรือไม่?"
          confirmText="ออกจากหน้านี้"
          cancelText="อยู่ต่อ"
          isDanger={true}
        />
      }
      sidebar={
        isEdit && product ? (
          <>
            <RecordMetaCard
              createdAt={product.created_at}
              createdBy={product.created_by}
              updatedAt={product.updated_at}
              updatedBy={product.updated_by}
            />
            <DetailSection title="ประวัติกิจกรรม" icon={<RiHistoryLine />}>
              <EntityActivityTimeline entityType="product" entityId={product.id} fieldLabels={PRODUCT_FIELD_LABELS} />
            </DetailSection>
          </>
        ) : undefined
      }
    >
      <DetailSection title="ข้อมูลสินค้า" icon={<RiShoppingBag3Line />}>
        <div>
          <ProductFormFields
            formData={formData}
            setFormData={setFormData}
            formErrors={formErrors}
            setFormErrors={setFormErrors}
            codeError={codeError}
          />
          <div className="mt-6 pt-4 border-t border-secondary-200 flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
            <Link
              to={cancelTo}
              className="inline-flex items-center justify-center px-5 py-2.5 border border-secondary-300 rounded-lg text-sm font-medium text-secondary-700 bg-white hover:bg-secondary-50 transition-all shadow-button"
            >
              <RiCloseLine className="w-4 h-4 mr-1.5" />
              ยกเลิก
            </Link>
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="inline-flex items-center justify-center px-6 py-2.5 border border-transparent rounded-lg text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500/20 transition-all shadow-button disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <RiCheckboxCircleLine className="w-4 h-4 mr-1.5" />
              {saving ? 'กำลังบันทึก...' : 'บันทึกข้อมูล'}
            </button>
          </div>
        </div>
      </DetailSection>
    </DetailPageLayout>
  );
};

export default ProductFormPage;
